import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch } from 'react-redux';
import { useLocation } from "react-router";
import { fetchEnd, fetchStart, useNotify } from "react-admin";
import { apiUrl } from "../../dataProvider";
import { LIST_COLORS } from "./Constants";

const COLORS = ["#8884d8", "#83a6ed", "#8dd1e1", "#82ca9d", "#a4de6c", "#d0ed57", "#ffc658"];

const buildMetricTrend = (metricData, mapper) => {
    if (!metricData) return undefined;
    return Object.keys(metricData).map(date => mapper(date, metricData[date]));
};

function GetGoogleAnalytics() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const notify = useNotify();
    const { search } = useLocation();

    useEffect(() => {
        let isMounted = true;

        const fetchGoogleMetrics = async (dates) => {
            setLoading(true);
            dispatch(fetchStart());

            try {
                const { data } = await axios.get(`${apiUrl}/google/metrics${dates}`);
                if (isMounted) {
                    setAnalytics(data);
                }
            } catch (error) {
                const response = error?.response;
                const message = response?.statusText;
                const status = response?.status;
                if (status === 401 || status === 403) {
                    window.location.replace('/#/login');
                }
                if (message) {
                    notify(message, 'warning');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
                dispatch(fetchEnd());
            }
        };

        fetchGoogleMetrics(search || '');

        return () => {
            isMounted = false;
        };
    }, [dispatch, notify, search]);

    const { totalUsersTrend, usersCount } = useMemo(() => {
        if (!analytics?.totalUsers) {
            return { totalUsersTrend: undefined, usersCount: undefined };
        }

        let count = 0;
        const trend = Object.keys(analytics.totalUsers).map(date => {
            count += analytics.totalUsers[date];
            return {
                date,
                users: analytics.totalUsers[date],
            };
        });

        return { totalUsersTrend: trend, usersCount: count };
    }, [analytics]);

    const { newUsers, totalNewUsers } = useMemo(() => {
        if (!analytics?.newUsers) {
            return { newUsers: undefined, totalNewUsers: undefined };
        }

        let count = 0;
        const trend = Object.keys(analytics.newUsers).map(date => {
            count += analytics.newUsers[date];
            return {
                date,
                visits: analytics.newUsers[date],
            };
        });

        return { newUsers: trend, totalNewUsers: count };
    }, [analytics]);

    const combinedUsers = useMemo(() => {
        if (!analytics?.newUsers || !analytics?.totalUsers) return undefined;

        return Object.keys(analytics.totalUsers).map(date => ({
            date,
            returning: analytics.totalUsers[date] - analytics.newUsers[date],
            newUsers: analytics.newUsers[date],
        }));
    }, [analytics]);

    const { bounceRateTrend, bounceRate } = useMemo(() => {
        if (!analytics?.bounceRate) {
            return { bounceRateTrend: undefined, bounceRate: undefined };
        }

        const dates = Object.keys(analytics.bounceRate);
        let totalBounceRate = 0;
        const trend = dates.map(date => {
            totalBounceRate += analytics.bounceRate[date];
            return {
                date,
                bounceRateTrend: analytics.bounceRate[date],
            };
        });

        return {
            bounceRateTrend: trend,
            bounceRate: dates.length ? Number((totalBounceRate / dates.length).toFixed(2)) : undefined,
        };
    }, [analytics]);

    const { pagesPerSessionTrend, avgPagesPerSession } = useMemo(() => {
        if (!analytics?.pagesPerSession) {
            return { pagesPerSessionTrend: undefined, avgPagesPerSession: undefined };
        }

        const dates = Object.keys(analytics.pagesPerSession);
        let totalCount = 0;
        const trend = dates.map(date => {
            totalCount += analytics.pagesPerSession[date];
            return {
                date,
                pagesPerSessionTrend: analytics.pagesPerSession[date],
            };
        });

        return {
            pagesPerSessionTrend: trend,
            avgPagesPerSession: dates.length ? Number((totalCount / dates.length).toFixed(2)) : undefined,
        };
    }, [analytics]);

    const { avgSessionDurationTrend, avgSessionDuration } = useMemo(() => {
        if (!analytics?.avgSessionDuration) {
            return { avgSessionDurationTrend: undefined, avgSessionDuration: undefined };
        }

        const dates = Object.keys(analytics.avgSessionDuration);
        let totalCount = 0;
        const trend = dates.map(date => {
            const time = analytics.avgSessionDuration[date];
            totalCount += time;
            const seconds = (time % 60) / 60;
            const minutes = Math.floor(time / 60);
            return {
                date,
                avgSessionDurationTrend: Number(minutes + seconds).toFixed(2),
            };
        });

        const consolidatedCount = dates.length ? totalCount / dates.length : 0;
        const seconds = Math.floor((((consolidatedCount % 60) / 60) * 100));
        const minutes = Math.floor(consolidatedCount / 60);
        const hours = Math.floor(consolidatedCount / 3600);
        const formattedHours = hours < 10 ? `0${hours}` : hours;
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

        return {
            avgSessionDurationTrend: trend,
            avgSessionDuration: `${formattedHours}:${formattedMinutes}:${seconds}`,
        };
    }, [analytics]);

    const totalSessions = useMemo(
        () => buildMetricTrend(analytics?.sessions, (date, sessions) => ({
            date,
            sessions,
        })),
        [analytics],
    );

    const avgPageLoadTime = useMemo(
        () => buildMetricTrend(analytics?.avgPageLoadTime, (date, time) => ({
            date,
            time,
        })),
        [analytics],
    );

    const topBrowsers = useMemo(() => {
        if (!analytics?.topBrowsers) return undefined;

        return analytics.topBrowsers.map((key, index) => ({
            value: key.sessions,
            name: key.browser,
            fill: LIST_COLORS[index] || LIST_COLORS[COLORS.length - 1],
        }));
    }, [analytics]);

    return {
        totalUsersTrend,
        newUsers,
        totalNewUsers,
        combinedUsers,
        usersCount,
        bounceRate,
        bounceRateTrend,
        pagesPerSessionTrend,
        avgPagesPerSession,
        avgSessionDurationTrend,
        avgSessionDuration,
        totalSessions,
        avgPageLoadTime,
        topReferrers: analytics?.topReferrers,
        mostVisitedPages: analytics?.mostVisitedPages,
        topBrowsers,
        loading,
    };
}

export default GetGoogleAnalytics;
